import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const QuoteView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const canManage = user?.role === 'boss' || user?.role === 'admin';

  const fetchQuote = React.useCallback(async () => {
    try {
      const res = await API.get(`/quotes/${id}`);
      setQuote(res.data);
    } catch (err) {
      toast.error('Quote not found');
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this quote and all its lines? This cannot be undone.')) return;
    try {
      await API.delete(`/quotes/${id}`);
      toast.success('Quote deleted');
      navigate('/quotes');
    } catch (err) {
      toast.error('Failed to delete quote');
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm(`Email quote ${quote.quote_number} to the customer? This will finalize it.`)) return;
    setSending(true);
    try {
      const res = await API.post(`/quotes/${id}/send`);
      const recipients = (res.data.sent_to || []).join(', ');
      toast.success(`Emailed to ${recipients}`);
      setQuote(prev => ({ ...prev, ...res.data.quote }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send quote');
    } finally {
      setSending(false);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this quote for editing? You can re-send it afterward.')) return;
    try {
      const res = await API.put(`/quotes/${id}/reopen`);
      setQuote(prev => ({ ...prev, ...res.data }));
      toast.success('Quote reopened');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reopen');
    }
  };

  const thStyle = {
    padding: '10px 8px', background: '#1A2A3A', color: 'white',
    fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
    textAlign: 'left', borderRight: '1px solid #F5A800'
  };
  const tdStyle = {
    padding: '8px', borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #f0f0f0', fontSize: '13px'
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!quote) return null;

  const isFinalized = quote.status === 'finalized';

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '600' }}>{quote.quote_number}</h1>
            {isFinalized ? (
              <span style={{ background: '#E9F7EF', color: '#15803D', border: '1px solid #A7D9BC', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                Finalized
              </span>
            ) : (
              <span style={{ background: '#FDF4DD', color: '#B07F00', border: '1px solid #F2D89A', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                Open
              </span>
            )}
          </div>
          <p style={{ color: '#5B6B7F', fontSize: '14px', marginTop: '4px' }}>
            Customer: <strong>{quote.customer_name}</strong>
            {isFinalized && quote.emailed_at ? ` · emailed ${new Date(quote.emailed_at).toLocaleDateString()}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/quotes')} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
            ← Back
          </button>
          {(canManage || !isFinalized) && (
            <button onClick={() => navigate(`/quotes/${id}/edit`)} style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
              Edit
            </button>
          )}
          <button
            onClick={() => window.open(`/api/quotes/${id}/export/pdf`, '_blank')}
            style={{ background: '#2632dc', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
          >
            Export PDF
          </button>
          <button
            onClick={() => window.open(`/api/quotes/${id}/export/excel`, '_blank')}
            style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
          >
            Export Excel
          </button>
          {canManage && (
            <button
              onClick={handleSendEmail}
              disabled={sending}
              style={{ background: '#1A2A3A', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: sending ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: sending ? 0.7 : 1 }}
            >
              {sending ? 'Sending…' : (isFinalized ? 'Resend Email' : 'Email to Customer')}
            </button>
          )}
          {canManage && isFinalized && (
            <button onClick={handleReopen} style={{ background: 'none', border: '1px solid #B07F00', color: '#B07F00', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
              Reopen
            </button>
          )}
          {canManage && (
            <button onClick={handleDelete} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Quote header details */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Quote #</p>
            <p style={{ fontWeight: '600' }}>{quote.quote_number}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Date</p>
            <p style={{ fontWeight: '600' }}>{new Date(quote.quote_date).toLocaleDateString()}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Valid Until</p>
            <p style={{ fontWeight: '600' }}>{quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Bill To</p>
            <p style={{ fontWeight: '600' }}>{quote.bill_to}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Estimated Total</p>
            <p style={{ fontWeight: '700', fontSize: '18px', color: '#15803D' }}>${parseFloat(quote.amount).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Lines table */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600' }}>
            Line Items ({quote.lines?.length || 0})
          </h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Zone', 'Weight', 'Bill. Wt', 'P/D', 'Base', 'FSC', 'Liftgate', 'Res', '2-Man', 'Inside', 'Unpack', 'Misc', 'Special', 'Total', 'Notes'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quote.lines?.map(s => (
                <tr key={s.line_id}>
                  <td style={tdStyle}>{s.zone}</td>
                  <td style={tdStyle}>{s.total_actual_weight}</td>
                  <td style={tdStyle}>{s.billable_weight}</td>
                  <td style={tdStyle}>{s.pickup_or_delivery}</td>
                  <td style={tdStyle}>${parseFloat(s.base_charge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.fuel_surcharge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.liftgate_fee).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.residential_charge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.two_man_charge).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.inside_charge_amt).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.unpack_charge_amt).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.misc_charges).toFixed(2)}</td>
                  <td style={tdStyle}>${parseFloat(s.special_charges).toFixed(2)}</td>
                  <td style={{ ...tdStyle, fontWeight: '600', color: '#15803D' }}>
                    ${parseFloat(s.final_total).toFixed(2)}
                  </td>
                  <td style={{ ...tdStyle, minWidth: '160px', whiteSpace: 'normal', color: '#5B6B7F' }}>
                    {[s.misc_notes, s.special_notes].map(x => (x ? String(x).trim() : '')).filter(Boolean).join(' — ') || '—'}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#F4F7FB' }}>
                <td colSpan={13} style={{ ...tdStyle, fontWeight: '600', textAlign: 'right' }}>
                  Estimated Total:
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#15803D', fontSize: '15px' }}>
                  ${parseFloat(quote.amount).toFixed(2)}
                </td>
                <td style={tdStyle}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuoteView;
